import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const PackageDetails = () => {
  const includedFeatures = [
    {
      category: "Design & Development",
      items: [
        "Responsive UI/UX design (desktop & mobile)",
        "Modern design system with consistent branding",
        "Clean, intuitive user flows"
      ]
    },
    {
      category: "Core Features",
      items: [
        "User authentication & account management",
        "Payment processing & subscription setup (Stripe/Paddle)",
        "Admin dashboard for user/content management",
        "Database design & data modeling"
      ]
    },
    {
      category: "AI Integrations",
      items: [
        "Chatbot implementation (customer support or onboarding)",
        "AI-powered features (automation, analysis, recommendations)",
        "Integration with GPT, Claude, or custom AI APIs"
      ]
    },
    {
      category: "Launch Readiness",
      items: [
        "Production deployment on your infrastructure",
        "Environment setup (staging & production)",
        "Documentation & handoff guide",
        "30 days of post-launch support"
      ]
    }
  ];

  const productTypes = [
    { title: "AI-Powered SaaS", description: "Analytics dashboards, workflow tools, data platforms, automation apps." },
    { title: "Chatbots & Assistants", description: "Customer support bots, onboarding assistants, internal knowledge bases." },
    { title: "Subscription Platforms", description: "SaaS products with tiered pricing, user accounts, and billing management." },
    { title: "Marketplaces", description: "Two-sided platforms connecting users, services, or products." },
    { title: "Internal Tools", description: "Admin panels, approval systems, reporting dashboards, workflow managers." },
    { title: "Lead-Gen Tools", description: "Calculators, interactive tools, landing pages with signup flows." }
  ];

  const exclusions = [
    "Custom AI model training from scratch",
    "Hardware or IoT integrations",
    "Blockchain or crypto-specific builds",
    "Apps requiring regulatory compliance (medical, financial, legal)",
    "Mobile-native apps (though we build responsive web apps that work great on mobile)"
  ];

  const addOns = [
    { emoji: "🎥", title: "Launch Video", description: "Promo video for social & Product Hunt" },
    { emoji: "🚀", title: "Product Hunt Launch Kit", description: "Design, copy, 20+ directory listings" },
    { emoji: "🛠", title: "Lead-Gen Micro-Tool", description: "Custom calculator or interactive tool" },
    { emoji: "🔧", title: "Post-Launch Support", description: "Monthly dev & iteration packages" }
  ];

  const faqs = [
    {
      question: "How much does the One Week MVP package cost?",
      answer: "$1,999 (Limited time: 50% off - now $999). Payment due after Saturday kickoff call."
    },
    {
      question: "What tech stack do you use?",
      answer: "Modern, scalable frameworks — typically Next.js, Node.js, Supabase, and AI APIs. We choose the stack based on your product requirements."
    },
    {
      question: "Can I bring my own designs?",
      answer: "Yes. If you have design assets, we'll incorporate them. If not, we design from scratch."
    },
    {
      question: "What happens if I need changes after scope freeze?",
      answer: "Changes after Sunday aren't included in the 7-day build. We can add them post-launch as part of support or a follow-up sprint."
    },
    {
      question: "Do you provide hosting?",
      answer: "We deploy to your infrastructure (Vercel, AWS, etc.). You own and control everything."
    },
    {
      question: "What's included in the documentation?",
      answer: "Setup guides, API documentation, database schemas, and deployment instructions."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "One Week MVP Build Package",
    "description": "Detailed one-week MVP build process for founders. Includes design, development, AI integrations, and deployment.",
    "provider": {
      "@type": "Organization",
      "name": "OneWeekMVPs"
    },
    "areaServed": "Worldwide",
    "offers": {
      "@type": "Offer",
      "price": "1999",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <SiteLayout>
      <SEO
        title="One Week MVP Build Package Details | What's Included in MVP Development Service"
        description="Detailed breakdown of our One Week MVP Build package. Learn what's included in MVP development service: design, development, AI integrations, authentication, payments, admin dashboard, and 30 days support."
        keywords="MVP build details, MVP service details, what's included in MVP development service, MVP development deliverables agency, detailed one-week MVP build process for founders, what's included in our rapid prototype service for agencies, AI-assisted MVP development deliverables list"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/package-details`}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading leading-tight">
              The One Week <span className="text-primary">MVP Build</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Everything you need to go from idea to launch-ready product in 7 days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" className="text-base px-8" asChild>
                <a href="https://checkout.dodopayments.com/buy/pdt_EGEdTJ9NOLaMWTDmwqhbv?session=sess_HIaYqixlwI">Reserve Your Seat</a>
              </Button>
              <div className="flex flex-col items-center gap-1">
                <div className="text-sm text-muted-foreground line-through">$1,999</div>
                <div className="text-sm text-muted-foreground">Only 4 slots per week • <span className="text-primary font-semibold">$999</span></div>
                <div className="text-primary text-xs font-medium">50% OFF Limited Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
                What you get in your <span className="text-primary">7-day build</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {includedFeatures.map((section, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-2xl">{section.category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {section.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="flex gap-3">
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center pt-8">
              <p className="text-xl text-foreground/90">
                No half-baked prototypes. No missing features. No "we'll finish that later."
              </p>
              <p className="text-lg text-muted-foreground mt-2">
                You get a real product that real users can use on day one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Build */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
                MVP types that fit this package
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productTypes.map((type, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-xl">{type.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{type.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center pt-8">
              <Button variant="outline" size="lg" asChild>
                <a href="https://cal.com/buildwithrishi/15min">Not Sure If Your Idea Fits? Book a Call</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Add-On Products */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Optional extras to <span className="text-primary">amplify your launch</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {addOns.map((addon, idx) => (
                <Card key={idx} className="border-border/50 text-center">
                  <CardHeader>
                    <div className="text-4xl mb-2">{addon.emoji}</div>
                    <CardTitle className="text-lg">{addon.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{addon.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button variant="outline">Add to My Package</Button>
            </div>
          </div>
        </div>
      </section>

      {/* What's Not Supported */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Let's be clear about what we <span className="text-primary">don't do</span>
              </h2>
            </div>

            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-2xl">We don't cover:</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {exclusions.map((item, idx) => (
                    <li key={idx} className="flex gap-3">
                      <X className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <div className="text-center space-y-4">
              <p className="text-lg text-muted-foreground">
                <strong className="text-foreground">Why These Limits?</strong><br />
                Because delivering high-quality MVPs in 7 days requires focus. We stick to what we can ship confidently.
              </p>
              <Button variant="outline" size="lg" asChild>
                <a href="https://cal.com/buildwithrishi/15min">Request Custom Estimate</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Package FAQs */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Package-specific <span className="text-primary">FAQs</span>
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}>
                  <AccordionTrigger className="text-left text-lg">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center">
              <Button variant="outline">More Questions? Visit Support</Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
              Ready to turn your idea into a product <span className="text-primary">this week?</span>
            </h2>
            <Button size="lg" className="text-base px-8" asChild>
              <a href="https://checkout.dodopayments.com/buy/pdt_EGEdTJ9NOLaMWTDmwqhbv?session=sess_HIaYqixlwI">Reserve My Seat</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default PackageDetails;
